import {
  MIN_PAYMENT_AMOUNT,
  NO_PAYMENT_METHOD_SELECTED,
  PAYMENT_METHOD_ERROR_MESSAGES,
  SPLIT_AMOUNTS_NOT_MATCHED,
  UNEDITABLE_SPLIT_PAYMENTS,
} from '~shared/features/checkout/constants';
import { getOrder } from '~shared/features/checkout/selectors/getOrder';
import { type ValidationError } from '~shared/features/checkout/types';
import { getItemsSubtotal } from '~shared/features/pricing/selectors/getOrderTotal';
import { getIsSplitPaymentEnabled } from '~shared/features/ui/selectors/getCart';
import { roundToTwo } from '~shared/helpers/currency';
import { useSelectorShared } from '~shared/store';

export const useCheckoutPaymentAmountErrors = () => {
  const isSplitPaymentEnabled = useSelectorShared(getIsSplitPaymentEnabled);
  const splitPaymentsTotal = useSelectorShared(getItemsSubtotal);
  const order = useSelectorShared(getOrder);
  const {
    payment_method_amount,
    payment_method_id: firstPaymentMethodID,
    second_payment_method_amount,
    second_payment_method_id: secondPaymentMethodID,
  } = order;
  const firstPaymentMethodAmount = Number(payment_method_amount);
  const secondPaymentMethodAmount = Number(second_payment_method_amount);

  const errors: ValidationError[] = [];

  if (!splitPaymentsTotal && firstPaymentMethodID && secondPaymentMethodID) {
    errors.push({
      key: UNEDITABLE_SPLIT_PAYMENTS,
      message: PAYMENT_METHOD_ERROR_MESSAGES.uneditable_split_payments,
    });
  }

  if (isSplitPaymentEnabled && splitPaymentsTotal) {
    if (!secondPaymentMethodID) {
      errors.push({
        key: NO_PAYMENT_METHOD_SELECTED,
        message: PAYMENT_METHOD_ERROR_MESSAGES.payment_method_does_not_exist,
        isSecondaryField: true,
      });
    }

    if (firstPaymentMethodAmount < MIN_PAYMENT_AMOUNT || secondPaymentMethodAmount < MIN_PAYMENT_AMOUNT) {
      errors.push({
        key: SPLIT_AMOUNTS_NOT_MATCHED,
        message: PAYMENT_METHOD_ERROR_MESSAGES.minimum_amount,
        isSecondaryField: secondPaymentMethodAmount < MIN_PAYMENT_AMOUNT,
      });
    }

    if (roundToTwo(firstPaymentMethodAmount + secondPaymentMethodAmount) !== splitPaymentsTotal) {
      errors.push({
        key: SPLIT_AMOUNTS_NOT_MATCHED,
        message: PAYMENT_METHOD_ERROR_MESSAGES.not_matched_full_amount,
        isSecondaryField: true,
      });
    }

    if (firstPaymentMethodID === secondPaymentMethodID) {
      errors.push({
        key: SPLIT_AMOUNTS_NOT_MATCHED,
        message: PAYMENT_METHOD_ERROR_MESSAGES.different_payment_methods,
        isSecondaryField: true,
      });
    }
  }

  return { paymentAmountValidationErrors: errors };
};
